import React from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Bot, ShieldAlert, Server, Signal, Cpu, Globe, MapPin, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function TruthyPill({ on, onLabel, offLabel, tone = "error" }) {
  if (!on) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-sand text-brand-subtle border border-brand-border">
        {offLabel}
      </span>
    );
  }
  const style =
    tone === "error"
      ? "bg-brand-error/15 text-brand-error border-brand-error/30"
      : "bg-brand-accent/15 text-brand-accent border-brand-accent/30";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${style}`}>
      {onLabel}
    </span>
  );
}

function copy(text) {
  navigator.clipboard?.writeText(text).then(
    () => toast("Copied"),
    () => toast.error("Couldn't copy"),
  );
}

function fmtWhen(iso) {
  try { return new Date(iso).toLocaleString("en-GB"); } catch { return iso; }
}

function eventLabel(ev) {
  const t = (ev.event_type || "").replace(/_/g, " ");
  const d = ev.event_data && Object.keys(ev.event_data).length
    ? " · " + Object.entries(ev.event_data)
        .filter(([, v]) => v !== null && v !== "")
        .slice(0, 3)
        .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`)
        .join(", ")
    : "";
  return t + d;
}

export default function EventDetailsModal({ eventId, onClose }) {
  const q = useQuery({
    queryKey: ["admin-event-detail", eventId],
    queryFn: async () => (await api.get(`/admin/telemetry/event/${eventId}`)).data,
    enabled: !!eventId,
    refetchOnWindowFocus: false,
  });

  if (!eventId) return null;

  const event = q.data?.event;
  const lookup = q.data?.ip_lookup;
  const recent = q.data?.recent_events || [];
  const isBotRow = !!event?.is_bot;
  const hostingOrProxy = !!(lookup?.hosting || lookup?.proxy);

  return (
    <div
      className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="event-details-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-brand-surface rounded-3xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col animate-fade-up"
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-3 flex items-start justify-between border-b border-brand-border/60">
          <div className="min-w-0">
            <div className="td-section-label">Recent events</div>
            <h3 className="font-heading text-xl text-brand-text mt-0.5 flex items-center gap-2">
              Event details
              {isBotRow && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-brand-error/15 text-brand-error border border-brand-error/30">
                  <Bot size={9} /> Bot
                </span>
              )}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-brand-subtle p-1 rounded-full hover:bg-brand-sand shrink-0"
            aria-label="Close"
            data-testid="event-details-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {q.isLoading && (
            <div className="text-sm text-brand-subtle flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Fetching IP lookup…
            </div>
          )}

          {event && (
            <>
              {/* Event row summary */}
              <section>
                <div className="td-section-label mb-1">Event</div>
                <div className="text-sm text-brand-text/90">{eventLabel(event)}</div>
                <div className="text-[11px] text-brand-subtle mt-0.5">{fmtWhen(event.timestamp)}</div>
                {event.bot_reason && (
                  <div className="text-[11px] text-brand-error/80 mt-1 italic">
                    Flag reason: {event.bot_reason}
                  </div>
                )}
              </section>

              {/* IP + UA */}
              <section>
                <div className="td-section-label mb-1.5">Source</div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-brand-subtle w-16 shrink-0">IP</span>
                    <code
                      className="font-mono text-sm px-2 py-0.5 rounded bg-brand-sand text-brand-text truncate flex-1"
                      data-testid="event-details-ip"
                      title={event.ip || "unknown"}
                    >
                      {event.ip || "—"}
                    </code>
                    {event.ip && (
                      <button
                        onClick={() => copy(event.ip)}
                        className="text-brand-subtle hover:text-brand-primary p-1"
                        title="Copy IP"
                        data-testid="event-details-copy-ip"
                      >
                        <Copy size={13} />
                      </button>
                    )}
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-[11px] text-brand-subtle w-16 shrink-0 pt-0.5">User-agent</span>
                    <code
                      className="font-mono text-[11px] px-2 py-1 rounded bg-brand-sand text-brand-text/80 leading-snug break-all flex-1"
                      data-testid="event-details-ua"
                    >
                      {event.user_agent || "—"}
                    </code>
                  </div>
                </div>
              </section>

              {/* IP lookup */}
              <section data-testid="event-details-ip-lookup">
                <div className="td-section-label mb-1.5 flex items-center gap-1">
                  <Globe size={11} /> IP lookup
                  {lookup?.cached && <span className="text-brand-subtle normal-case tracking-normal">(cached)</span>}
                </div>
                {!lookup || lookup.status !== "success" ? (
                  <p className="text-sm text-brand-subtle">
                    {lookup?.status === "local" ? "Local address — no external lookup." : "IP lookup unavailable."}
                  </p>
                ) : (
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin size={12} className="text-brand-subtle shrink-0" />
                      <span className="text-brand-text/90">
                        {[lookup.city, lookup.region, lookup.country].filter(Boolean).join(", ") || "Unknown"}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Server size={12} className="text-brand-subtle shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="text-brand-text/90 truncate" title={lookup.isp || ""}>{lookup.isp || "—"}</div>
                        {lookup.org && lookup.org !== lookup.isp && (
                          <div className="text-[11px] text-brand-subtle truncate" title={lookup.org}>{lookup.org}</div>
                        )}
                        {lookup.as_ && (
                          <div className="text-[10px] text-brand-subtle/80 truncate">{lookup.as_}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      <TruthyPill on={lookup.hosting} onLabel="Data-centre / hosting" offLabel="Not hosting" />
                      <TruthyPill on={lookup.proxy} onLabel="Proxy / VPN" offLabel="Not proxy" />
                      <TruthyPill on={lookup.mobile} onLabel="Mobile carrier" offLabel="Not mobile" tone="info" />
                    </div>
                    {hostingOrProxy && (
                      <div className="flex items-start gap-2 mt-2 p-2 rounded-lg bg-brand-error/5 border border-brand-error/20 text-[11px] text-brand-error/85">
                        <ShieldAlert size={13} className="shrink-0 mt-0.5" />
                        <span>
                          {lookup.hosting && lookup.proxy
                            ? "This IP is a hosting/proxy — very likely a scraper."
                            : lookup.hosting
                            ? "This IP is a data-centre — unusual for a real walker."
                            : "This IP is a proxy/VPN — a real user may still be behind it, so verify before banning."}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* Last 5 events from same source */}
              <section>
                <div className="td-section-label mb-1.5 flex items-center gap-1">
                  <Signal size={11} /> Last {recent.length} event{recent.length === 1 ? "" : "s"} from this source
                </div>
                {recent.length === 0 ? (
                  <p className="text-sm text-brand-subtle">No prior events recorded.</p>
                ) : (
                  <ol className="space-y-1.5" data-testid="event-details-recent-list">
                    {recent.map((r, i) => (
                      <li
                        key={i}
                        className={`text-sm rounded-lg px-2.5 py-1.5 border ${
                          r.is_bot
                            ? "bg-brand-error/5 border-brand-error/20"
                            : "bg-brand-sand/60 border-brand-border/60"
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          {r.is_bot && <Cpu size={11} className="text-brand-error shrink-0" />}
                          <span className="text-brand-text/90 truncate">{eventLabel(r)}</span>
                          <span className="ml-auto text-[10px] text-brand-subtle whitespace-nowrap">
                            {fmtWhen(r.timestamp)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-brand-border/60 flex justify-end">
          <button
            onClick={onClose}
            className="td-btn-secondary !py-2 !px-4 !text-sm"
            data-testid="event-details-done"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
